<script setup lang="ts">
  import * as v from 'valibot'
  import type { Form, FormSubmitEvent } from '#ui/types'

  // Idioma
  const { locale, t } = useI18n()

  // Props
  const props = defineProps({
    showLoginLink: {
      type: Boolean,
      default: true
    },
    submitLabel: {
      type: String,
      default: ''
    },
    showSeparator: {
      type: Boolean,
      default: true
    },
    showSocialButtons: {
      type: Boolean,
      default: true
    },
    linkLogin: {
      type: Boolean,
      default: true
    },
    hotelId: {
      type: Number,
      default: null
    }
  })

  // Emits
  const emit = defineEmits(['register-success', 'register-error', 'show-login'])

  // Validation
  const schema = v.object({
    email: v.pipe(
      v.string(),
      v.email(t('errors.login.email'))
    ),
    password: v.pipe(
      v.string(),
      v.minLength(8, t('errors.login.password')),
      v.regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/, t('errors.login.passwordComplexity'))
    )
  })

  type Schema = v.InferOutput<typeof schema>

  const state = reactive({
    email: '',
    password: ''
  })

  const form = ref<Form<Schema>>()
  const error_message = ref<string>('')
  const isLoading = ref(false)
  const showPassword = ref(false)

  // Password strength requirements
  const passwordRequirements = computed(() => [
    { text: t('labels.passwordRequirements.length') || 'At least 8 characters', met: state.password.length >= 8 },
    { text: t('labels.passwordRequirements.uppercase') || 'At least 1 uppercase letter', met: /[A-Z]/.test(state.password) },
    { text: t('labels.passwordRequirements.lowercase') || 'At least 1 lowercase letter', met: /[a-z]/.test(state.password) },
    { text: t('labels.passwordRequirements.number') || 'At least 1 number', met: /\d/.test(state.password) }
  ])

  const passwordStrength = computed(() => {
    if (!state.password) return 0
    return passwordRequirements.value.filter(req => req.met).length
  })

  const passwordStrengthColor = computed(() => {
    if (passwordStrength.value === 0) return 'neutral'
    if (passwordStrength.value === 1) return 'error'
    if (passwordStrength.value === 2) return 'warning'
    if (passwordStrength.value === 3) return 'warning'
    return 'success'
  })

  async function onSubmit(event: FormSubmitEvent<Schema>) {
    isLoading.value = true
    error_message.value = ''
    form.value!.clear()
    
    const { data, error } = await useFetch(`/auth/byhours/register`, {
      method: 'POST',
      body: {
        email: event.data.email,
        password: event.data.password,
        register_channel: 1,
        login_with: 'byhours',
        locale
      }
    })

    if (error.value) {
      error_message.value = error.value.data.statusMessage
      emit('register-error', error_message.value)
    }

    if (data.value) {
      const { fetch } = useUserSession()
      await fetch() // Actualiza la session
      
      emit('register-success', data.value)
    }
    
    isLoading.value = false
  }

  watch(state, (value) => {
    if(!value.email && !value.password) form.value!.clear()
    error_message.value = ''
  })

</script>

<template>
  <div class="w-full flex flex-col gap-4">
    <UForm ref="form" :schema="schema" class="flex flex-col space-y-4" :state="state" @submit="onSubmit">
      
      <UFormField name="email">
        <UInput 
          v-model="state.email" 
          autocomplete="username" 
          :placeholder="t('labels.login.placeholder.email')" 
          size="xl" 
        />
      </UFormField>

      <UFormField>
        <UInput 
          v-model="state.password" 
          autocomplete="new-password" 
          :type="showPassword ? 'text' : 'password'"
          :placeholder="t('labels.login.placeholder.password')" 
          size="xl"
          :ui="{ trailing: 'pe-1' }"
        >
          <template #trailing>
            <UButton
              color="neutral"
              variant="link"
              size="sm"
              :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              :aria-pressed="showPassword"
              aria-controls="password"
              @click="showPassword = !showPassword"
            />
          </template>
        </UInput>
        <div v-if="state.password" class="mt-2 space-y-2">
          <UProgress 
            :model-value="passwordStrength" 
            :max="4" 
            :color="passwordStrengthColor"
            size="xs"
          />
          <ul class="text-xs text-muted space-y-1">
            <li 
              v-for="(requirement, index) in passwordRequirements" 
              :key="index"
              class="flex items-center gap-2"
            >
              <UIcon 
                :name="requirement.met ? 'i-lucide-check' : 'i-lucide-x'" 
                :class="requirement.met ? 'text-success' : 'text-muted'"
                class="size-4 shrink-0"
              />
              <span :class="requirement.met ? 'text-success' : ''">
                {{ requirement.text }}
              </span>
            </li>
          </ul>
        </div>
      </UFormField>

      <UAlert
        v-if="error_message"
        icon="i-heroicons-exclamation-circle"
        :title="error_message"
        color="error"
        variant="soft"
      />

      <UButton 
        type="submit"
        class="justify-center"
        color="primary"
        variant="solid"
        size="xl"
        :loading="isLoading"
        :disabled="isLoading"
      >
        {{ submitLabel || t('labels.login.continue') }}
      </UButton>

    </UForm>

    <div v-if="showLoginLink" class="flex items-center gap-2 w-full justify-center text-sm mt-4">
      <span>{{ t('labels.login.register.account') }}</span>
      <ULink v-if="linkLogin" :to="`/${locale}/login`" class="text-primary">{{ t('labels.login.session') }}</ULink>
      <UButton 
        v-else
        color="primary"
        variant="link"
        size="md"
        @click="emit('show-login')"
      >
        {{ t('labels.login.session') }}
      </UButton>
    </div>

    <USeparator v-if="showSeparator" :label="t('labels.login.o')" class="my-4" />

    <LoginButtons v-if="showSocialButtons" :hotel-id="hotelId" />

  </div>
</template> 